import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useEffect, useMemo, useState } from 'react'
import { API_BASE } from '../config'
import { policyBodyToHtml } from '../utils/policyHtml'

type PolicyPayload = {
  slug: string
  title: string
  content: string
  updatedAt?: string
}

function formatDate(v?: string) {
  if (!v) return new Date().toLocaleDateString('en-IN')
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return new Date().toLocaleDateString('en-IN')
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
}

type Props = {
  slug: 'privacy' | 'terms' | 'refund'
  fallbackTitle: string
  fallbackHtml: string
}

export default function PolicyDocumentPage({ slug, fallbackTitle, fallbackHtml }: Props) {
  const [policy, setPolicy] = useState<PolicyPayload | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    fetch(`${API_BASE}/api/public/policy/${slug}`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled || !data?.success || !data?.policy) return
        setPolicy({
          slug: data.policy.slug,
          title: data.policy.title || fallbackTitle,
          content: data.policy.content || '',
          updatedAt: data.policy.updatedAt,
        })
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [slug, fallbackTitle])

  const title = policy?.title || fallbackTitle
  const html = useMemo(
    () => policyBodyToHtml(policy?.content || fallbackHtml),
    [policy?.content, fallbackHtml]
  )

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="pt-28 sm:pt-32 pb-16 sm:pb-20 px-4 sm:px-6 lg:px-8"
    >
      <div className="container-narrow">
        {loading && !policy ? (
          <div className="space-y-4 animate-pulse">
            <div className="h-10 w-2/3 rounded-xl bg-slate-200/70 dark:bg-slate-700/40" />
            <div className="h-4 w-40 rounded bg-slate-200/60 dark:bg-slate-700/30" />
            <div className="h-64 rounded-2xl bg-slate-200/50 dark:bg-slate-700/30" />
          </div>
        ) : (
          <>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary dark:text-white mb-3 sm:mb-4">
              {title}
            </h1>
            <p className="text-slate-500 dark:text-slate-400 text-sm mb-8">
              Last updated: {formatDate(policy?.updatedAt)}
            </p>
            <div className="card-glass p-5 sm:p-8">
              <div
                className="prose prose-slate dark:prose-invert max-w-none prose-headings:text-slate-800 dark:prose-headings:text-slate-100 prose-p:text-slate-600 dark:prose-p:text-slate-400 prose-a:text-primary dark:prose-a:text-accent"
                dangerouslySetInnerHTML={{ __html: html }}
              />
            </div>
          </>
        )}
        <Link to="/" className="inline-block mt-8 text-primary dark:text-accent font-medium hover:underline">
          ← Back to home
        </Link>
      </div>
    </motion.div>
  )
}
